import type { ILogger } from '../logging/Logger';
import type { IProjectService, ServiceResult, PaginationParams, PaginatedResult } from '../interfaces/IDataService';
import { publicAPI, type Project as APIProject } from '../../../lib/api';
import { ValidationError } from '../../../utils/errorHandler';
import type { Project } from '../../../types/index';

/**
 * ProjectService Implementation
 * 
 * Provides project management functionality with proper API integration.
 * Uses the publicAPI.getPublishedProjects() method for data retrieval.
 * 
 * Features:
 * - Full project listing with pagination
 * - Technology-based filtering
 * - Status-based filtering (completed, in_progress, planned)
 * - Featured projects highlighting
 * - Comprehensive error handling and logging
 * 
 * @author Ertugrul Kücükkaya
 * @version 1.0.0
 */
export class ProjectService implements IProjectService {
  private readonly logger: ILogger;

  constructor(logger: ILogger) {
    this.logger = logger;
  }

  /**
   * Convert API Project to interface Project
   */
  private mapApiProject(apiProject: APIProject): Project {
    return apiProject as unknown as Project;
  }

  /**
   * Get all projects with pagination and optional filtering
   */
  async getAll(params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<Project>>> {
    try {
      this.logger.debug('ProjectService.getAll called', 'ProjectService', { params });

      const { page = 1, limit = 10, sortBy = 'created_at', sortOrder = 'desc' } = params;

      // Validate pagination parameters
      if (page < 1 || limit < 1 || limit > 100) {
        return {
          success: false,
          error: 'Invalid pagination parameters. Page must be >= 1, limit must be between 1 and 100.'
        };
      }

      const apiProjects = await publicAPI.getPublishedProjects();
      let projects = apiProjects.map(project => this.mapApiProject(project));

      // Apply sorting
      projects = this.applySorting(projects, sortBy, sortOrder);

      // Apply pagination
      const total = projects.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedProjects = projects.slice(startIndex, endIndex);

      const result: PaginatedResult<Project> = {
        items: paginatedProjects,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Projects retrieved successfully', 'ProjectService', {
        total,
        page,
        itemCount: paginatedProjects.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in ProjectService.getAll', 'ProjectService', { 
        error: (error as Error).message,
        params 
      });
      
      if (error instanceof ValidationError) {
        return { success: false, error: error.message };
      }
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching projects' 
      };
    }
  }

  /**
   * Get a project by ID
   */
  async getById(id: string): Promise<ServiceResult<Project>> {
    try {
      this.logger.debug('ProjectService.getById called', 'ProjectService', { id });

      if (!id || typeof id !== 'string') {
        return { success: false, error: 'Invalid project ID provided' };
      }

      const apiProjects = await publicAPI.getPublishedProjects();
      const apiProject = apiProjects.find(project => project.id === id);

      if (!apiProject) {
        this.logger.warn('Project not found', 'ProjectService', { id });
        return { success: false, error: 'Project not found' };
      }

      const project = this.mapApiProject(apiProject);
      this.logger.debug('Project retrieved successfully', 'ProjectService', { 
        id, 
        title: project.title 
      });

      return { success: true, data: project };

    } catch (error) {
      this.logger.error('Error in ProjectService.getById', 'ProjectService', { 
        error: (error as Error).message,
        id 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching the project' 
      };
    }
  }

  /**
   * Get current/active projects (alias for in_progress)
   */
  async getCurrent(): Promise<ServiceResult<Project[]>> {
    try {
      this.logger.debug('ProjectService.getCurrent called', 'ProjectService');

      const apiProjects = await publicAPI.getPublishedProjects();
      const currentProjects = apiProjects
        .filter(project => project.status === 'in_progress')
        .map(project => this.mapApiProject(project))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      this.logger.debug('Current projects retrieved', 'ProjectService', {
        count: currentProjects.length
      });

      return { success: true, data: currentProjects };

    } catch (error) {
      this.logger.error('Error in ProjectService.getCurrent', 'ProjectService', { 
        error: (error as Error).message 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching current projects' 
      };
    }
  }

  /**
   * Get completed projects with pagination
   */
  async getCompleted(params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<Project>>> {
    try {
      this.logger.debug('ProjectService.getCompleted called', 'ProjectService', { params });

      const apiProjects = await publicAPI.getPublishedProjects();
      const completedProjects = apiProjects
        .filter(project => project.status === 'completed')
        .map(project => this.mapApiProject(project));

      // Sort by creation date (most recent first)
      const sortedProjects = completedProjects.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });

      // Apply pagination
      const { page = 1, limit = 10 } = params;
      const total = sortedProjects.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedProjects = sortedProjects.slice(startIndex, endIndex);

      const result: PaginatedResult<Project> = {
        items: paginatedProjects,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Completed projects retrieved', 'ProjectService', {
        total,
        page,
        itemCount: paginatedProjects.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in ProjectService.getCompleted', 'ProjectService', { 
        error: (error as Error).message,
        params 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching completed projects' 
      };
    }
  }

  /**
   * Get featured projects
   */
  async getFeatured(): Promise<ServiceResult<Project[]>> {
    try {
      this.logger.debug('ProjectService.getFeatured called', 'ProjectService');

      const apiProjects = await publicAPI.getPublishedProjects();
      const featuredProjects = apiProjects
        .filter(project => project.status === 'completed')
        .map(project => this.mapApiProject(project))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      this.logger.debug('Featured projects retrieved', 'ProjectService', {
        count: featuredProjects.length
      });

      return { success: true, data: featuredProjects };

    } catch (error) {
      this.logger.error('Error in ProjectService.getFeatured', 'ProjectService', { 
        error: (error as Error).message 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching featured projects' 
      };
    }
  }

  /**
   * Get projects by status with pagination
   */
  async getByStatus(status: string, params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<Project>>> {
    try {
      this.logger.debug('ProjectService.getByStatus called', 'ProjectService', { status, params });

      if (!status) {
        return { success: false, error: 'Status parameter is required' };
      }

      const validStatuses = ['completed', 'in_progress', 'planned'];
      if (!validStatuses.includes(status)) {
        return { 
          success: false, 
          error: `Invalid status. Must be one of: ${validStatuses.join(', ')}` 
        };
      }

      const apiProjects = await publicAPI.getPublishedProjects();
      const filteredProjects = apiProjects
        .filter(project => project.status === status)
        .map(project => this.mapApiProject(project));

      // Apply sorting (most recent first)
      const sortedProjects = filteredProjects.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });

      // Apply pagination
      const { page = 1, limit = 10 } = params;
      const total = sortedProjects.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedProjects = sortedProjects.slice(startIndex, endIndex);

      const result: PaginatedResult<Project> = {
        items: paginatedProjects,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Projects by status retrieved', 'ProjectService', {
        status,
        total,
        page,
        itemCount: paginatedProjects.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in ProjectService.getByStatus', 'ProjectService', { 
        error: (error as Error).message,
        status,
        params 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching projects by status' 
      };
    }
  }

  /**
   * Get projects by technology/stack with pagination
   */
  async getByTechnology(technology: string, params: Partial<PaginationParams> = {}): Promise<ServiceResult<PaginatedResult<Project>>> {
    try {
      this.logger.debug('ProjectService.getByTechnology called', 'ProjectService', { technology, params });

      if (!technology || typeof technology !== 'string') {
        return { success: false, error: 'Technology parameter is required' };
      }

      const apiProjects = await publicAPI.getPublishedProjects();
      // technologies field no longer exists in the DB schema; return empty result
      const filteredProjects = apiProjects
        .filter(() => false)
        .map(project => this.mapApiProject(project));

      // Sort by creation date (most recent first)
      const sortedProjects = filteredProjects.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });

      // Apply pagination
      const { page = 1, limit = 10 } = params;
      const total = sortedProjects.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedProjects = sortedProjects.slice(startIndex, endIndex);

      const result: PaginatedResult<Project> = {
        items: paginatedProjects,
        total,
        page,
        limit,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      };

      this.logger.debug('Projects by technology retrieved', 'ProjectService', {
        technology,
        total,
        page,
        itemCount: paginatedProjects.length
      });

      return { success: true, data: result };

    } catch (error) {
      this.logger.error('Error in ProjectService.getByTechnology', 'ProjectService', { 
        error: (error as Error).message,
        technology,
        params 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching projects by technology' 
      };
    }
  }

  /**
   * Get unique technologies used across all projects (alias method)
   */
  async getTechnologies(): Promise<ServiceResult<string[]>> {
    return this.getAllTechnologies();
  }

  /**
   * Get all unique technologies used across projects
   */
  async getAllTechnologies(): Promise<ServiceResult<string[]>> {
    try {
      this.logger.debug('ProjectService.getTechnologies called', 'ProjectService');

      const apiProjects = await publicAPI.getPublishedProjects();
      void apiProjects; // technologies field no longer exists in the DB schema
      const allTechnologies: string[] = [];

      this.logger.debug('Technologies retrieved', 'ProjectService', {
        count: allTechnologies.length,
        technologies: allTechnologies
      });

      return { success: true, data: allTechnologies };

    } catch (error) {
      this.logger.error('Error in ProjectService.getTechnologies', 'ProjectService', { 
        error: (error as Error).message 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while fetching technologies' 
      };
    }
  }

  /**
   * Check if a project exists
   */
  async exists(id: string): Promise<ServiceResult<boolean>> {
    try {
      this.logger.debug('ProjectService.exists called', 'ProjectService', { id });

      if (!id || typeof id !== 'string') {
        return { success: false, error: 'Invalid project ID provided' };
      }

      const apiProjects = await publicAPI.getPublishedProjects();
      const exists = apiProjects.some(project => project.id === id);

      this.logger.debug('Project existence checked', 'ProjectService', { id, exists });

      return { success: true, data: exists };

    } catch (error) {
      this.logger.error('Error in ProjectService.exists', 'ProjectService', { 
        error: (error as Error).message,
        id 
      });
      
      return { 
        success: false, 
        error: 'An unexpected error occurred while checking project existence' 
      };
    }
  }

  /**
   * Create a new project (placeholder - requires admin API)
   */
  async create(_project: Partial<Project>): Promise<ServiceResult<Project>> {
    this.logger.warn('ProjectService.create called but not implemented', 'ProjectService');
    return { 
      success: false, 
      error: 'Project creation is not available in the current implementation' 
    };
  }

  /**
   * Update a project (placeholder - requires admin API)
   */
  async update(_id: string, _updates: Partial<Project>): Promise<ServiceResult<Project>> {
    this.logger.warn('ProjectService.update called but not implemented', 'ProjectService');
    return { 
      success: false, 
      error: 'Project updates are not available in the current implementation' 
    };
  }

  /**
   * Delete a project (placeholder - requires admin API)
   */
  async delete(_id: string): Promise<ServiceResult<boolean>> {
    this.logger.warn('ProjectService.delete called but not implemented', 'ProjectService');
    return { 
      success: false, 
      error: 'Project deletion is not available in the current implementation' 
    };
  }

  /**
   * Apply sorting to projects array
   */
  private applySorting(projects: Project[], sortBy: string, sortOrder: string): Project[] {
    return projects.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'updated_at': {
          const updatedA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          const updatedB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          comparison = updatedA - updatedB;
          break;
        }
        case 'status': {
          const statusOrder: Record<string, number> = { 'completed': 3, 'in_progress': 2, 'planned': 1 };
          comparison = (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0);
          break;
        }
        case 'satisfaction':
          // Note: ratings property doesn't exist in interface, fallback to 0
          comparison = 0;
          break;
        case 'complexity':
          // Note: ratings property doesn't exist in interface, fallback to 0
          comparison = 0;
          break;
        default:
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }

      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }
} 